import React, {
  FunctionComponent,
  useState,
  ReactNode,
  useEffect,
  ChangeEvent,
} from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import apis from '../../apis';
import {
  HeaderDisplayKeysWithType,
  InlineEditableTable,
  TextField,
  OverflowWithTooltip,
  DateTimeField,
} from '../../components';
import {
  PHIDisease,
  Semester,
  StudentPHIAll,
  YearSemClasses,
} from '../../model';
import { InputDropdownGroup } from '../../components';
import { toast } from 'react-toastify';
import { I18N } from '../../i18n-raw';
import { DEFAULT_DATETIME_FORMAT } from '../../utils/date';

type Props = {
  editable: boolean;
  deleteable: boolean;
  diseases: PHIDisease[];
  student: StudentPHIAll;
  semesters: Semester[];
  yearSems: YearSemClasses[];
  onApiError: (e: any) => void;
  onValueUpdated: () => void;
  onValueDelete?: () => void;
};
const transformTextInput = (
  property: string,
  v: unknown,
  i: number,
  editing?: boolean
) => {
  if (!editing) {
    // 非編輯狀態維持原本顯示邏輯
    return wrapOverflow(v as string);
  }

  // 編輯狀態且是 description 欄位
  if (property === 'description') {
    return <TextField property={property} as="textarea" rows={5} />;
  }

  return <TextField property={property} />;
};

const inlineDiseaseTableHeader: HeaderDisplayKeysWithType<PHIDisease>[] = [
  { property: 'studentId', display: '統編', style: { width: '200px' } },
  { property: 'diseaseId', display: '診斷代號', style: { width: '80px' } },
  { property: 'diseaseName', display: '疾病名稱', style: { width: '110px' } },
  {
    property: 'description',
    display: '陳述',
    onRender: transformTextInput.bind(null, 'description'),
  },
  {
    property: 'treatment',
    display: '處置',
    onRender: transformTextInput.bind(null, 'treatment'),
  },
  {
    property: 'enterDate',
    display: '輸入日期',
    onRender: (v, i, e) =>
      e ? (
        <DateTimeField property="enterDate" />
      ) : (
        v.toFormat(DEFAULT_DATETIME_FORMAT)
      ),
  },
];

function wrapOverflow(content: ReactNode) {
  return <OverflowWithTooltip>{content}</OverflowWithTooltip>;
}
export const DiseaseSection: FunctionComponent<Props> = ({
  editable,
  deleteable,
  diseases,
  student,
  semesters,
  yearSems,
  onApiError,
  onValueUpdated,
}) => {
  const currentSemester = semesters.find((s) => s.isNow);
  const [inputing, setInputing] = useState(false);
  const [isduplicateModal, setIsDuplicateModal] = useState(false);
  const [diseaseItems, setdiseaseItems] = useState<object>();
  const [diseaseDropDownItems, setdiseaseDropDownItems] =
    useState<Record<number, string>>();
  const [diseaseDropDown, setDiseaseDropDown] = useState<number>();
  const [isStudentDiseaseValid, setIsStudentDiseaseValid] =
    useState<boolean>(false);
  const [insertBtn, setInsertBtn] = useState<boolean>(true);
  const [treatment, setTreatment] = useState<string>('');
  const [year, setYear] = useState<number>();
  const [sem, setSem] = useState<number>();
  const [studentDiseaseData, setStudentDiseaseData] = useState<any[]>([]);
  const wh = diseases.find(
    (s) => s.sem === currentSemester?.sem && s.year === currentSemester?.year
  );

  useEffect(() => {
    // 目前學期、學年
    const isNow = semesters.filter((y) => y.isNow == true)[0];
    const studentDisease = diseases.map((x) => {
    return { ...x, studentId: student.pid };
  });
    setYear(isNow.year);
    setSem(isNow.sem);
    // 個人疾病下拉選單
    setdiseaseDropDownItems(I18N.StudentDiseaseType);
    setStudentDiseaseData(studentDisease);
  }, [semesters]);

  useEffect(() => {
    setInsertBtn(false);
  }, [diseaseDropDown]);

  useEffect(() => {
    // 檢查疾病代號
    setIsStudentDiseaseValid(false);
    if (diseaseDropDown) {
      if (I18N.StudentDiseaseType[diseaseDropDown]) {
        setIsStudentDiseaseValid(true);
        apis
          .GetDiseaseDropDown()
          .then((s) => {
            if (s.length) {
              s.forEach((i) => {
                if (i.id == diseaseDropDown) {
                  setTreatment(i.treatment);
                }
              });
            }
          })
          .catch(onApiError);
      }
    }
  }, [diseaseDropDown]);

  yearSems.filter(
    (s) => !diseases.some((wh) => wh.sem === s.sem && wh.year === s.year)
  );

  diseases.sort((a, b) => a.sem - b.sem + (a.year - b.year) * 100);

  function onValueUpdate(value: PHIDisease) {
    setStudentDiseaseData((prev) =>
      prev.map((item) => (item.id === value.id ? { ...item, ...value } : item))
    );

    apis
      .updateStudentDisease(
        value?.id,
        value?.diseaseId,
        value?.diseaseName,
        value.description,
        value.treatment ?? "",
        value.enterDate ? value.enterDate.toString() : '',
        value.yearClassId
      )
      .then(() => {getStudentDiseaseByPid();})
      .catch(onApiError);
  }

  function onValueDelete(value: PHIDisease) {
    apis
      .deleteStudentDisease(
        value?.id,
        value?.diseaseId,
        value?.diseaseName,
        value.description,
        value.treatment,
        value.enterDate ? value.enterDate.toString() : '',
        value.yearClassId
      )
      // .then(onValueUpdated)
      .then(getStudentDiseaseByPid)
      .catch(onApiError);
  }

  function insertDiseaseCheck() {
    // 檢查診斷代號
    if (isStudentDiseaseValid) {
      insertDisease();
    } else {
      toast.error('診斷代號錯誤!', {});
    }
  }

  function checkDuplicateDisease() {
    const isDuplicate = studentDiseaseData.some(
      (disease) => disease.diseaseId === diseaseDropDown
    );

    if(isDuplicate) {
      setIsDuplicateModal(true);
    } else {
      insertDisease();
    }
  }

  function insertDisease() {
    if (diseaseDropDown) {
      // 送出api
      if (year && sem) {
        toast
          .promise(
            apis.insertPhiDisease(
              year,
              sem,
              '',
              0,
              0,
              0,
              student.pid,
              diseaseDropDown,
              '', // 陳述
              treatment,
              new Date().toLocaleString('sv').slice(0, 10),
              1
            ),
            {
              pending: '資料新增中......',
              success: '新增成功！',
            }
          )
          .then(()=> {getStudentDiseaseByPid();})
          .catch(onApiError);
      }
    }
  }

  function getStudentDiseaseByPid() {
    if (diseaseDropDown) {
      if (year && sem) {
        apis.GetStudentDiseaseByPid(
          year,
          sem,
          student.pid
        )
        .then((r)=> {     
          setStudentDiseaseData(r.map((x) => {
            return { ...x.studentDisease};
          }));
        })
        .catch(onApiError);
      }
    }
  }

  return (
    <>
      <div>
        個人疾病史
        <Button
          key="cell-edit"
          className="border-0 px-2 py-1"
          variant="outline-primary"
          size="sm"
          onClick={() => {
            setInputing(true);
          }}
        >
          <span className="feather icon-edit" />
        </Button>
      </div>
      <Modal
        show={inputing}
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        dialogClassName="modal-dialog-full"
        centered
      >
        <Modal.Header closeButton onHide={() =>{
          onValueUpdated();
          setInputing(false)}
        }>
          <Modal.Title id="contained-modal-title-vcenter">
            個人疾病史 -{' '}
            <strong>
              {student.seat} {student.name}
            </strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="overflow-auto">
          <Row className="px-2 mb-3">
            疾病代號：
            <Col sm={3}>
              <InputDropdownGroup
                className={'p-1 text-center mr-2'}
                name="diseaseId"
                type="number"
                options={diseaseDropDownItems || {}}
                defaultShow="無異常"
                value={diseaseDropDown || ''}
                onChange={(
                  e: ChangeEvent<
                    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                  >
                ) => {
                  setDiseaseDropDown(parseInt(e.target.value));
                }}
                onBlur={() => {}}
                onFocus={() => {}}
              />
            </Col>
            <Button disabled={insertBtn} onClick={checkDuplicateDisease}>
              新增
            </Button>
          </Row>
          <InlineEditableTable
            values={studentDiseaseData}
            headers={inlineDiseaseTableHeader}
            onValueUpdate={onValueUpdate}
            onValueDelete={onValueDelete}
            onRowRender={(v) => ({
              editable: editable,
              deleteable: deleteable,
            })}
          />
        </Modal.Body>
      </Modal>
      <Modal
        show={isduplicateModal}
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        dialogClassName="modal-dialog-full"
        centered
      >
        <Modal.Header closeButton onHide={() =>{
          setIsDuplicateModal(false)}
        }>
          <Modal.Title id="contained-modal-title-vcenter">
            資料重複
            <strong>
              {student.seat} {student.name}
            </strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="overflow-auto">
          <div>已有相同疾病代號{diseaseDropDown}的資料，請問是否新增?</div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsDuplicateModal(false);
            }}
          >
            取消
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              insertDisease();
              setIsDuplicateModal(false);
            }}
          >
            確定新增
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
